import { EXPORT_FONTS } from './fonts';

const W = 1650;
const H = 1550;

// Any URL (blob:, relative, http:) -> data: URI.
async function urlToDataUri(url) {
    const blob = await (await fetch(url)).blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Rasterises the preview <svg> at its native 1650x1550 and downloads a PNG.
 * Every <image> is inlined as a data URI first, because an SVG drawn through an
 * <img> cannot load external or blob: resources.
 */
export default async function exportPng(svgEl, filename) {
    if (!svgEl) return;

    const clone = svgEl.cloneNode(true);
    clone.setAttribute('viewBox', `0 0 ${W} ${H}`);
    clone.setAttribute('width', String(W));
    clone.setAttribute('height', String(H));
    clone.style.cssText = '';

    // An SVG drawn through <img> can't see the page's @font-face rules, so embed them.
    try {
        const css = (await Promise.all(EXPORT_FONTS.map(async (f) => {
            const [w2, w1] = await Promise.all([urlToDataUri(f.woff2), urlToDataUri(f.woff)]);
            return `@font-face { font-family: '${f.family}'; src: url('${w2}') format('woff2'), `
                + `url('${w1}') format('woff'); font-weight: normal; font-style: normal; }`;
        }))).join('\n');
        const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        style.textContent = css;
        clone.insertBefore(style, clone.firstChild);
    } catch (e) {
        console.warn('UniformBuilder: could not embed fonts for export', e);
    }

    await Promise.all(Array.from(clone.querySelectorAll('image')).map(async (img) => {
        const href = img.getAttribute('href');
        if (!href || href.startsWith('data:')) return;
        try {
            img.setAttribute('href', await urlToDataUri(href));
        } catch (e) {
            console.warn('UniformBuilder: could not inline image for export', href, e);
        }
    }));

    const svgUrl = URL.createObjectURL(
        new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' })
    );
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;

    try {
        await new Promise((resolve, reject) => {
            const img = new Image(W, H);
            img.onload = () => {
                canvas.getContext('2d').drawImage(img, 0, 0, W, H);
                resolve();
            };
            img.onerror = () => reject(new Error('Could not render the garment SVG'));
            img.src = svgUrl;
        });
    } catch (e) {
        console.error('UniformBuilder: PNG export failed', e);
        return;
    } finally {
        URL.revokeObjectURL(svgUrl);
    }

    const link = document.createElement('a');
    link.download = filename;
    link.href = canvas.toDataURL('image/png');
    link.click();
}
